import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { XMarkIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import useWilmaCustomers from "../../../data/useWilmaCustomers";
import { IQuote } from "../../../models/Quote";
import BreakdownView from "./BreakdownView";
import RawJsonViewer from "./RawJsonViewer";
import { BreakdownSide, bodyConditionLabel, engineConditionLabel, formatPrice, quoteTypeLabel } from "./calculationBreakdown";

export const SegmentedToggle = <T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; disabled?: boolean }[];
}) => (
  <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        disabled={option.disabled}
        onClick={() => onChange(option.value)}
        className={clsx(
          "rounded px-3 py-1 text-sm font-medium",
          value === option.value ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-gray-100",
          option.disabled && "cursor-not-allowed opacity-40 hover:bg-transparent"
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const QuoteInputs = ({ quote }: { quote: Pick<IQuote, "vehicleRegistration" | "postcode" | "mileage" | "engineCondition" | "bodyCondition" | "alloyWheels"> }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-gray-50 p-3 text-sm sm:grid-cols-3">
    {[
      ["Reg", quote.vehicleRegistration],
      ["Postcode", quote.postcode],
      ["Mileage", quote.mileage?.toLocaleString("en-GB")],
      ["Engine", engineConditionLabel(quote.engineCondition)],
      ["Body", bodyConditionLabel(quote.bodyCondition)],
      ["Alloy wheels", quote.alloyWheels ? "Yes" : "No"],
    ].map(([label, value]) => (
      <div key={label} className="flex gap-2">
        <dt className="text-gray-500">{label}:</dt>
        <dd className="font-medium text-gray-900">{value}</dd>
      </div>
    ))}
  </dl>
);

/** SAV only: the stored calculation behind this quote's prices, readable or as raw json. */
export default function QuoteBreakdownModal({
  quote,
  open,
  setOpen,
}: {
  quote: IQuote;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { customers } = useWilmaCustomers();
  const customerName = (id?: string | null) =>
    customers.data?.data.find((c) => c.id.toLowerCase() === id?.toLowerCase())?.name ?? "Unknown customer";

  const hasCollection = !!quote.collectedCustomerId;
  const hasDropOff = !!quote.dropOffCustomerId;

  const [side, setSide] = useState<BreakdownSide>(hasCollection ? "collection" : "dropOff");
  const [view, setView] = useState<"readable" | "raw">("readable");

  useEffect(() => {
    if (open) setSide(hasCollection ? "collection" : "dropOff");
  }, [open, hasCollection]);

  const current = useMemo(
    () =>
      side === "collection"
        ? { customerId: quote.collectedCustomerId, type: quote.collectedType, price: quote.priceCollected, json: quote.collectedCalculationJson }
        : { customerId: quote.dropOffCustomerId, type: quote.dropOffType, price: quote.priceDropOff, json: quote.dropOffCalculationJson },
    [side, quote]
  );

  return (
    <Modal width="max-w-6xl" open={open} setOpen={setOpen}>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Quote breakdown</h3>
            <p className="text-sm text-gray-500">
              How this quote's prices were worked out when it was priced. Settings changed since then aren't reflected here.
            </p>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <QuoteInputs quote={quote} />

        {!hasCollection && !hasDropOff ? (
          <p className="text-sm text-gray-500">No customer priced this quote, so there's no breakdown to show.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SegmentedToggle
                value={side}
                onChange={setSide}
                options={[
                  { value: "collection", label: "Collection", disabled: !hasCollection },
                  { value: "dropOff", label: "Drop Off", disabled: !hasDropOff },
                ]}
              />
              <SegmentedToggle
                value={view}
                onChange={setView}
                options={[
                  { value: "readable", label: "Breakdown" },
                  { value: "raw", label: "Raw JSON" },
                ]}
              />
            </div>

            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
              <span>
                <span className="text-gray-500">Customer: </span>
                <span className="font-medium text-gray-900">{customerName(current.customerId)}</span>
              </span>
              <span>
                <span className="text-gray-500">Price type: </span>
                <span className="font-medium text-gray-900">{quoteTypeLabel(current.type)}</span>
              </span>
              <span>
                <span className="text-gray-500">Price: </span>
                <span className="font-semibold text-gray-900">{formatPrice(current.price)}</span>
              </span>
            </div>

            {view === "readable" ? (
              <BreakdownView calculationJson={current.json} side={side} winningType={current.type} total={current.price} />
            ) : (
              <RawJsonViewer json={current.json} />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
